/**
 * Chart Studio → drop overlay zones (drag Data models fields onto the open
 * chart), as pure data. Figma: `z2UTps4F1dKNh1bbD4LtYI` node `6433:561051`.
 *
 * Same contract as `dataModelFieldSections.ts`: structure and copy live here,
 * the drag / hover state lives at the usage site keyed by `id`. What a drop on
 * a zone does to the binding is `bindingChangeToPatch`'s `drop-fields` case —
 * never here, so a tile can't advertise a rule the drop doesn't follow.
 */

/** Stable zone key; also the `zone` of a `drop-fields` binding change. */
export type ChartDropZoneId = "auto" | "xAxis" | "yAxis" | "colorBy" | "stackBy";

export interface ChartDropZoneConfig {
  id: ChartDropZoneId;
  /** Tile heading (Figma: "X-axis"). */
  title: string;
  /** Muted text shown after the title. */
  titleSuffix?: string;
  /** Tile helper line under the heading. */
  hint: string;
  /** `full` spans both grid columns (Auto); `half` is one cell of the 2×2. */
  span: "full" | "half";
  /**
   * The binding has somewhere to put this zone's fields. `false` renders the
   * tile disabled ("Coming soon") until the backend adds the slot.
   */
  backed: boolean;
}

/**
 * Most measures the Y zone takes. Each measure gets its own independent y
 * axis; Vega-Lite puts the first on the left and every later one on the
 * right, so a third axis would draw over the second. The backend's
 * `binding.y` `maxItems` is still 4 — this is the stricter client cap.
 */
export const CHART_Y_AXIS_MAX_FIELDS = 2;

/** The Y zone's helper line, shared by the drop overlay and Configure. */
export const CHART_Y_AXIS_HINT = `Drop a measure (up to ${CHART_Y_AXIS_MAX_FIELDS})`;

/**
 * Registry order is the overlay's render order: Auto across the top, then the
 * 2×2 grid left-to-right, top-to-bottom. Stack by is unbacked — `BindingApi`
 * has no stack field yet (flagged to the backend).
 */
export const chartDropZones = [
  {
    id: "auto",
    title: "Auto",
    /** Muted text after the title (design: "Auto · best fit"). */
    titleSuffix: "best fit",
    hint: "Let MagicBI pick the chart & placement",
    span: "full",
    backed: true,
  },
  { id: "xAxis", title: "X axis", hint: "Drop a date or category", span: "half", backed: true },
  { id: "yAxis", title: "Y axis", hint: CHART_Y_AXIS_HINT, span: "half", backed: true },
  {
    id: "colorBy",
    title: "Color",
    hint: "Drop a category to color by",
    span: "half",
    backed: true,
  },
  {
    id: "stackBy",
    title: "Tagged by",
    hint: "Drop a category to split the marks",
    span: "half",
    backed: true,
  },
] as const satisfies readonly ChartDropZoneConfig[];

/**
 * A disabled tile's hint in place of its helper line: why a drop there would
 * change nothing. Which one applies is `dropZoneDisabledHint`'s call.
 */
export const chartDropZoneDisabledHints = {
  comingSoon: "Coming soon",
  loading: "Loading the chart…",
  needsMeasure: "Needs a measure",
  needsCategory: "Needs a category",
  noDetailSlot: "This chart type has no tag",
  yAxisFull: (max: number) => `Y axis is full (${max})`,
  alreadyOnChart: "Already on the chart",
  nothingToPlace: "Nothing new to place",
} as const;
